import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';

import {
  BORDER, TEXT_SECONDARY, WARNING, WARNING_BG, WARNING_BODY, WARNING_BORDER, WARNING_TITLE,
} from '../common/tokens';

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const nameSx = {
  fontWeight: 700,
  fontFamily: 'monospace',
  background: 'rgba(0,0,0,0.06)',
  padding: '0 4px',
  borderRadius: '3px',
};

/**
 * Confirmation for the one rename that can't be undone: typing an existing tag's
 * name folds this tag into that one. Shown instead of committing, so the merge
 * is always a second, deliberate action.
 */
export default function MergeBanner({ targetTag, newName, onConfirm, onCancel }) {
  const theme = useTheme();

  return (
    <Box sx={{
      animation: `${slideDown} 0.18s ease`,
      background: WARNING_BG,
      border: `1px solid ${WARNING_BORDER}`,
      borderRadius: '6px',
      padding: '10px 14px',
      marginTop: '8px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      maxWidth: 420,
      boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
    }}>
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke={WARNING}
        strokeWidth="2"
        style={{ flexShrink: 0, marginTop: 1 }}
      >
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <Box sx={{ flex: 1 }}>
        <Box sx={{
          fontSize: 13, fontWeight: 600, color: WARNING_TITLE, marginBottom: '3px',
        }}>
          Merge tags?
        </Box>
        <Box sx={{ fontSize: 12, color: WARNING_BODY, lineHeight: 1.5 }}>
          <Box component="span" sx={nameSx}>{newName}</Box>
          {' '}
          already exists. Renaming will merge
          {' '}
          <Box component="span" sx={nameSx}>{targetTag}</Box>
          {' '}
          into it — this can&apos;t be undone.
        </Box>
        <Box sx={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <Box
            component="button"
            type="button"
            onClick={onConfirm}
            sx={{
              background: theme.palette.primary.main,
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              padding: '5px 14px',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Merge
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onCancel}
            sx={{
              background: 'transparent',
              color: TEXT_SECONDARY,
              border: `1px solid ${BORDER}`,
              borderRadius: '5px',
              padding: '5px 14px',
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancel
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
